import PropTypes from 'prop-types';
import React, { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconNoInternet } from '../../../assets/images';
import { CONNECTION_STATE_CONN_FAILED, CONNECTION_STATE_CONNECTING, CONNECTION_STATE_ERROR_OCCURED } from '../../../Helpers/Chat/Constant';
import { isConnStateEqualTo } from '../../../Helpers/Utility';
import { getRecordingStatus, setActiveConversationChannel } from '../../../Slice';
import { DefaultChannel, ForwardFooter, RecentChannelItem, SearchBox, SmartListView } from '../../components';
import { ImageCreateChannel } from '../../rootAccess';
import { handleChannelNavigation } from '../../utils';
import { useRecentChannels } from '../../utils/customHooks/useRecentChannels';

const RecentChannelList = React.memo(function RecentChannelList({
   isInModal,
   isSelectable,
   maxSelectionCount,
   scopeKey = 'page',
   component,
}) {
   const dispatch = useDispatch();
   const loadingRef = useRef(null);
   const {
      channelList: recentChannels = [],
      loading,
      hasMore,
      searchText,
      loadMore,
      updateSearchText,
   } = useRecentChannels(scopeKey, loadingRef);
   
   const isAppOnline = useSelector(state => state?.appOnlineStatus?.isOnline);
   const connectionState = useSelector(state => state?.ConnectionStateData?.data);
   const handleChannelClick = useCallback(
      channel => {
         if (getRecordingStatus()) return;
         dispatch(setActiveConversationChannel(channel));
      },
      [dispatch],
   );

   const renderItem = useCallback(
      ({ item, index }) => {
         return (
            <RecentChannelItem
               channel={item}
               onClick={handleChannelClick}
               isSelectable={isSelectable}
               maxSelectionCount={maxSelectionCount}
               scopeKey={scopeKey}
               index={index}
               totalItems={recentChannels.length}
            />
         );
      },
      [handleChannelClick, isSelectable, maxSelectionCount, scopeKey, recentChannels.length],
   );

   const renderEmptyView = useCallback(() => {
      return (
         <DefaultChannel
            Title={searchText ? 'No results found' : 'Engage with your People'}
            Description={
               searchText
                  ? ''
                  : 'The community feature connects users for interaction and collaboration, fostering engagement through discussions and updates.'
            }
            btnText={searchText ? '' : 'Explore More'}
            btnClick={() => !searchText && handleChannelNavigation({ pane: 'exploreChannels' })}
            Image={searchText ? '' : ImageCreateChannel}
            alt={searchText ? '' : 'Create Channel Icon'}
         />
      );
   }, [searchText]);


   const handleDispatchSearchText = useCallback(
      val => {
         updateSearchText(val);
      },
      [updateSearchText],
   );
   const showOffline = (!isAppOnline || isConnStateEqualTo([
      CONNECTION_STATE_CONNECTING,
      CONNECTION_STATE_CONN_FAILED,
      CONNECTION_STATE_ERROR_OCCURED,
   ], connectionState));


   return (
      <>
         {((recentChannels && recentChannels.length > 0) || searchText) && (
            <SearchBox onChange={handleDispatchSearchText} val={searchText} />
         )}
         {component === 'forward' &&
            <ForwardFooter />
         }
         {showOffline && (
            <div className="networkoffline">
               <i className="icon">
                  <IconNoInternet />
               </i>
               <div>
                  <h4>Computer not connected</h4>
                  <p>Make sure your computer has an active internet connection.....</p>
               </div>
            </div>
         )}
         <div
            className="channels-list-container">
            <SmartListView
               id="recent-channel-list"
               renderItem={renderItem}
               data={recentChannels}
               ListEmptyComponent={!loading && renderEmptyView()}
               onLoadBottom={loadMore}
               hasMoreBottom={hasMore}
               loading={loading}
               enableAutoLoadMore={true}
            />
         </div>
      </>
   );
});

RecentChannelList.propTypes = {
   isInModal: PropTypes.bool,
   isSelectable: PropTypes.bool,
   maxSelectionCount: PropTypes.number,
   scopeKey: PropTypes.string,
};

export default RecentChannelList;
